import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect } from 'react';
import type { User } from 'src/api/generated';
import { useCurrentUser as useAdminCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useAuthBootstrap } from 'src/modules/shared/contexts/auth-bootstrap-context';
import { isRecord } from 'src/modules/shared/utils/types.util';

interface UseCurrentUser {
  currentUser: User | null;
  loading: boolean;
}

const hasRolesClaim = (
  value: unknown
): value is { 'https://llamadapro.app/roles'?: string[] } => {
  return isRecord(value);
};

export const useCurrentUser = (): UseCurrentUser => {
  const { loading: loadingBootstrap, ready } = useAuthBootstrap();
  const { user, loading: loadingCurrentUser } = useAdminCurrentUser(ready);

  return {
    currentUser: user,
    loading: loadingBootstrap || (ready && loadingCurrentUser),
  };
};

/**
 * Custom hook to retrieve the user's permissions.
 *
 * This hook uses Auth0 to silently fetch the access token, decodes it to extract the user's permissions,
 * and provides the permissions along with loading state.
 */
export const useUserPermissions = () => {
  const { permissions, loading } = useAuthBootstrap();
  return {
    permissions,
    loading,
  };
};

/**
 * Custom hook to retrieve the user's roles.
 *
 * This hook accesses the authenticated user from Auth0 and extracts roles from a custom claim.
 * Provides the roles along with loading state.
 */
export const useUserRoles = () => {
  const { user, isLoading } = useAuth0();
  const roles = hasRolesClaim(user)
    ? user['https://llamadapro.app/roles'] ?? []
    : [];

  return { roles, loading: isLoading };
};

export const useFilterAgent = (users: User[], currentUser: User | null) => {
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);

  useEffect(() => {
    const exists = currentUser
      ? users.some((user) => user.id === currentUser.id)
      : false;

    if (currentUser && exists) {
      setSelectedAgentId(currentUser.id);
    }
  }, [currentUser, users]);

  return { selectedAgentId, sendAgentSelection: setSelectedAgentId };
};
